import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Modal, Select, Space, message } from 'antd';
import SortAndCountPanel from '../../../modules/sortAndCount/SortAndCountPanel';
import { useSortAndCountSession } from '../../../modules/sortAndCount/useSortAndCountSession';
import type { SortCountTargetDTO, TempSortCountSessionDTO } from '../../../types/sortAndCount';
import {
  listTempSortCountSessions,
  removeTempSortCountSession,
  upsertTempSortCountSession,
} from '../../../services/sortCount';
import { buildTempSessionPayload } from '../../../modules/sortAndCount/adapter';

const parseTargetsFromText = (value: string): SortCountTargetDTO[] => {
  const lines = String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines
    .map((line) => {
      const [code, label, qtyText, mark] = line.split(',').map((part) => String(part || '').trim());
      const qty = Math.max(0, Number(qtyText || 0));
      return {
        target_code: String(code || '').toUpperCase(),
        target_label: label || String(code || '').toUpperCase(),
        expected_qty: qty,
        sorting_mark: mark || undefined,
      };
    })
    .filter((row) => !!row.target_code && row.expected_qty > 0);
};

const SortAndCountWorkbench: React.FC = () => {
  const [allSessions, setAllSessions] = useState<TempSortCountSessionDTO[]>(() => listTempSortCountSessions());
  const [selectedId, setSelectedId] = useState<string>('');
  const selectedSession = useMemo(
    () => allSessions.find((session) => String(session?.id || '') === String(selectedId || '')) || null,
    [allSessions, selectedId],
  );

  const [targetImportText, setTargetImportText] = useState<string>('');
  const session = useSortAndCountSession({
    targets: selectedSession?.targets || [],
    events: selectedSession?.events || [],
  });
  const {
    targets,
    events,
    rows,
    summary,
    snapshot,
    validation,
    applyScan,
    removeEvent,
    clearEvents,
    setTargets,
    setEvents,
  } = session;

  useEffect(() => {
    setTargets(selectedSession?.targets || []);
    setEvents(selectedSession?.events || []);
  }, [selectedSession?.id, setEvents, setTargets]);

  const reloadSessions = () => {
    setAllSessions(listTempSortCountSessions());
  };

  const createNewSession = () => {
    const id = `tmp_sc_${Date.now()}`;
    const payload = buildTempSessionPayload({
      id,
      name: `临时分拣清点-${new Date().toLocaleString()}`,
      targets: [],
      events: [],
    });
    upsertTempSortCountSession(payload);
    reloadSessions();
    setSelectedId(id);
    setTargets([]);
    setEvents([]);
    message.success('已创建临时会话');
  };

  const saveCurrentSession = () => {
    const id = selectedSession?.id || `tmp_sc_${Date.now()}`;
    const payload = buildTempSessionPayload({
      id,
      name: selectedSession?.name || `临时分拣清点-${new Date().toLocaleString()}`,
      targets,
      events,
      context: selectedSession?.context,
    });
    upsertTempSortCountSession({
      ...payload,
      created_at: selectedSession?.created_at || payload.created_at,
    });
    reloadSessions();
    setSelectedId(id);
    message.success('会话已保存');
  };

  const deleteCurrentSession = async () => {
    if (!selectedSession?.id) return;
    const ok = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '确认删除当前临时会话？',
        content: '删除后无法恢复。',
        okText: '删除',
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    removeTempSortCountSession(selectedSession.id);
    reloadSessions();
    setSelectedId('');
    setTargets([]);
    setEvents([]);
    message.success('已删除');
  };

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Card title="临时分拣清点工作台">
        <Space wrap>
          <Select
            placeholder="选择历史会话"
            style={{ width: 360 }}
            allowClear
            value={selectedId || undefined}
            onChange={(value) => setSelectedId(String(value || ''))}
            options={allSessions.map((session) => ({
              value: session.id,
              label: `${session.name}（${new Date(session.updated_at).toLocaleString()}）`,
            }))}
          />
          <Button type="primary" onClick={createNewSession}>新建会话</Button>
          <Button onClick={saveCurrentSession}>保存会话</Button>
          <Button danger onClick={deleteCurrentSession} disabled={!selectedSession?.id}>删除会话</Button>
        </Space>
      </Card>

      <Card title="目标导入（独立页增强能力）">
        <Input.TextArea
          value={targetImportText}
          onChange={(e) => setTargetImportText(e.target.value)}
          rows={5}
          placeholder="每行一个目标：目标编码,目标名,目标数量,分拣标识(可选)"
        />
        <div style={{ marginTop: 10 }}>
          <Space>
            <Button
              onClick={() => {
                const parsed = parseTargetsFromText(targetImportText);
                if (parsed.length === 0) {
                  message.warning('未解析出有效目标，请检查文本格式');
                  return;
                }
                setTargets(parsed);
                clearEvents();
                message.success(`已导入 ${parsed.length} 个目标`);
              }}
            >
              用导入结果替换目标
            </Button>
            <Button onClick={() => setTargetImportText('')}>清空输入</Button>
          </Space>
        </div>
      </Card>

      <SortAndCountPanel
        title="临时分拣清点"
        instructionCode={selectedSession?.name || '临时会话'}
        canOperate
        rows={rows}
        events={events}
        summary={summary}
        snapshot={snapshot}
        validation={validation}
        onScan={applyScan}
        onRemoveEvent={removeEvent}
        onClearEvents={clearEvents}
        onSubmitRequest={async () => {
          if (!validation.pass) {
            message.error(validation.errors.join('；') || '当前不可提交');
            return;
          }
          Modal.success({
            title: '临时分拣清点结果',
            content: `目标 ${summary.total_targets} 项，已完成 ${summary.done_targets} 项，差额 ${summary.diff_total}。`,
          });
        }}
        extraActions={<Button onClick={saveCurrentSession}>保存草稿</Button>}
      />
    </div>
  );
};

export default SortAndCountWorkbench;

